// Customizable Area Start
import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  MenuItem,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Grid,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Tabs,
  Tab,
  styled,
} from "@material-ui/core";
import NavigationIcon from "@material-ui/icons/Navigation";
import VisibilityIcon from "@material-ui/icons/Visibility";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloseIcon from "@material-ui/icons/Close";
import { Alert } from "@material-ui/lab";

import DriverTrackerController, {
  Props,
  IDriverTracker,
  TRACKER_STATES,
  ATTENDANCE_STATUSES,
} from "./DriverTrackerController.web";
// Customizable Area End

export default class DriverTracker extends DriverTrackerController {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }

  // Customizable Area Start
  mapNode: HTMLDivElement | null = null;
  mapInstance: any = null;
  mapNodeForInstance: HTMLDivElement | null = null;
  markers: Map<number, any> = new Map();
  infoWindow: any = null;
  mapReadyTimer: ReturnType<typeof setInterval> | undefined;
  hasFitBounds: boolean = false;

  async componentDidMount() {
    await super.componentDidMount();
    this.waitForGoogleMaps();
  }

  async componentWillUnmount() {
    await super.componentWillUnmount();
    if (this.mapReadyTimer) clearInterval(this.mapReadyTimer);
    this.markers.forEach((marker) => marker.setMap(null));
    this.markers.clear();
  }

  componentDidUpdate() {
    this.syncMarkers();
  }

  // Switching tabs unmounts the map container, so on the way back React hands us
  // a different node. The old map is bound to the detached one and would render
  // nothing — discard it and build again on the node we were just given.
  setMapNode = (node: HTMLDivElement | null) => {
    this.mapNode = node;
    if (!node) return;

    if (this.mapInstance && this.mapNodeForInstance !== node) {
      this.markers.forEach((marker) => marker.setMap(null));
      this.markers.clear();
      this.mapInstance = null;
      this.mapNodeForInstance = null;
      this.infoWindow = null;
      this.hasFitBounds = false;
    }

    // Go through the same retry loop the first load uses. Building straight from
    // the ref lands mid-commit, before the freshly shown container is laid out,
    // and the Maps API reads that size as it builds — which leaves a map with no
    // tiles. Retrying until it takes is what actually works here.
    this.waitForGoogleMaps();
  };

  // The Maps script is loaded with `defer` from packages/web/public/index.html,
  // so it is usually not ready by the time this mounts.
  waitForGoogleMaps = () => {
    if (this.mapReadyTimer) {
      clearInterval(this.mapReadyTimer);
      this.mapReadyTimer = undefined;
    }
    if (this.initMap()) return;
    this.mapReadyTimer = setInterval(() => {
      if (this.initMap() && this.mapReadyTimer) {
        clearInterval(this.mapReadyTimer);
        this.mapReadyTimer = undefined;
      }
    }, 400);
  };

  initMap = () => {
    const google = (window as any).google;
    if (!google?.maps || !this.mapNode || this.mapInstance) return Boolean(this.mapInstance);

    this.mapNodeForInstance = this.mapNode;
    this.mapInstance = new google.maps.Map(this.mapNode, {
      // Riyadh — only the initial view; bounds are fitted once drivers arrive.
      center: { lat: 24.7136, lng: 46.6753 },
      zoom: 6,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    });
    this.infoWindow = new google.maps.InfoWindow();
    this.syncMarkers();
    return true;
  };

  syncMarkers = () => {
    const google = (window as any).google;
    if (!google?.maps || !this.mapInstance) return;

    const drivers = this.getMappableDrivers();
    const seen = new Set<number>();

    drivers.forEach((driver) => {
      const attrs = driver.attributes;
      const position = {
        lat: Number(attrs.location.latitude),
        lng: Number(attrs.location.longitude),
      };
      if (Number.isNaN(position.lat) || Number.isNaN(position.lng)) return;

      seen.add(attrs.account_id);
      const colors = this.getStateColor(attrs.tracker_state);
      // A stale fix still shows its last known pin, greyed — dropping the driver
      // off the map would read as "gone" rather than "not reporting".
      const fill = attrs.location.fresh ? colors.color : "#9CA3AF";

      let marker = this.markers.get(attrs.account_id);
      if (!marker) {
        marker = new google.maps.Marker({ map: this.mapInstance });
        marker.addListener("click", () => {
          this.infoWindow.setContent(this.buildInfoContent(driver));
          this.infoWindow.open(this.mapInstance, marker);
        });
        this.markers.set(attrs.account_id, marker);
      }

      marker.setPosition(position);
      marker.setTitle(attrs.driver.full_name || "Driver");
      marker.setIcon({
        path: google.maps.SymbolPath.CIRCLE,
        scale: 9,
        fillColor: fill,
        fillOpacity: 1,
        strokeColor: "#FFFFFF",
        strokeWeight: 2,
      });
    });

    this.markers.forEach((marker, accountId) => {
      if (!seen.has(accountId)) {
        marker.setMap(null);
        this.markers.delete(accountId);
      }
    });

    if (!this.hasFitBounds && this.markers.size > 0) {
      const bounds = new google.maps.LatLngBounds();
      this.markers.forEach((marker) => bounds.extend(marker.getPosition()));
      this.mapInstance.fitBounds(bounds);
      if (this.markers.size === 1) this.mapInstance.setZoom(14);
      this.hasFitBounds = true;
    }
  };

  buildInfoContent = (driver: IDriverTracker) => {
    const attrs = driver.attributes;
    const name = this.escapeHtml(attrs.driver.full_name || "Driver");
    const state = this.escapeHtml(this.formatLabel(attrs.tracker_state));
    const age = this.escapeHtml(this.formatAge(attrs.location));
    const jobs = attrs.workload.active_requests;
    return `<div style="font-family:Montserrat;font-size:13px;line-height:1.6">
      <div style="font-weight:600;font-size:14px">${name}</div>
      <div>State: ${state}</div>
      <div>Last fix: ${age}</div>
      <div>Active jobs: ${jobs}</div>
    </div>`;
  };

  escapeHtml = (value: string) =>
    value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  summariseStores = (stores: { id: number; store_name: string }[]) => {
    if (!stores.length) return "--";
    const shown = stores.slice(0, 2).map((store) => store.store_name).join(", ");
    const rest = stores.length - 2;
    return rest > 0 ? `${shown} +${rest} more` : shown;
  };

  getStateColor = (state: string) => {
    const colorMap: { [key: string]: { bg: string; color: string } } = {
      idle: { bg: "#D1FAE5", color: "#065F46" },
      on_job: { bg: "#DBEAFE", color: "#1E40AF" },
      on_break: { bg: "#FEF3C7", color: "#92400E" },
      no_signal: { bg: "#FFEDD5", color: "#9A3412" },
      offline: { bg: "#F3F4F6", color: "#4B5563" },
    };
    return colorMap[state] || { bg: "#F3F4F6", color: "#374151" };
  };

  renderSummary() {
    const { summary } = this.state;
    if (!summary) return null;

    const tiles = [
      { label: "Drivers", value: summary.drivers },
      { label: "Online", value: summary.online },
      { label: "Active Jobs", value: summary.active_requests },
      { label: "Checked In", value: summary.checked_in },
      { label: "Checked Out", value: summary.checked_out },
      { label: "Location Stale", value: summary.location_stale },
    ];

    return (
      <Box data-test-id="driverSummary">
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          {tiles.map((tile) => (
            <Grid item xs={6} sm={4} md={2} key={tile.label}>
              <Box style={webStyle.tile}>
                <Typography style={webStyle.tileLabel}>{tile.label}</Typography>
                <Typography style={webStyle.tileValue}>{tile.value}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* summary reflects the whole filtered set, not just this page */}
        <Box style={webStyle.pillRow}>
          {TRACKER_STATES.map((state) => {
            const colors = this.getStateColor(state);
            const isActive = this.state.filterState === state;
            const count =
              summary.by_state[state as keyof typeof summary.by_state] ?? 0;
            return (
              <Chip
                key={state}
                data-test-id={`statePill-${state}`}
                label={`${this.formatLabel(state)} · ${count}`}
                onClick={() =>
                  this.handleFilterChange(
                    "filterState",
                    isActive ? "" : state
                  )
                }
                style={{
                  backgroundColor: colors.bg,
                  color: colors.color,
                  fontFamily: "Montserrat",
                  fontWeight: 600,
                  cursor: "pointer",
                  border: isActive ? `2px solid ${colors.color}` : "2px solid transparent",
                }}
              />
            );
          })}
        </Box>
      </Box>
    );
  }

  renderFilters() {
    const {
      searchText,
      filterState,
      filterAttendance,
      filterAvailable,
    } = this.state;

    return (
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            data-test-id="driverSearch"
            fullWidth
            variant="outlined"
            size="small"
            label="Search name or phone"
            value={searchText}
            onChange={(event) => this.handleSearchChange(event.target.value)}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            data-test-id="stateFilter"
            fullWidth
            select
            variant="outlined"
            size="small"
            label="State"
            value={filterState}
            onChange={(event) =>
              this.handleFilterChange("filterState", event.target.value)
            }
          >
            <MenuItem value="">All</MenuItem>
            {TRACKER_STATES.map((state) => (
              <MenuItem key={state} value={state}>
                {this.formatLabel(state)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            data-test-id="attendanceFilter"
            fullWidth
            select
            variant="outlined"
            size="small"
            label="Attendance"
            value={filterAttendance}
            onChange={(event) =>
              this.handleFilterChange("filterAttendance", event.target.value)
            }
          >
            <MenuItem value="">All</MenuItem>
            {ATTENDANCE_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {this.formatLabel(status)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            data-test-id="availableFilter"
            fullWidth
            select
            variant="outlined"
            size="small"
            label="Available"
            value={filterAvailable}
            onChange={(event) =>
              this.handleFilterChange("filterAvailable", event.target.value)
            }
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <Button
            data-test-id="clearFilters"
            variant="outlined"
            onClick={this.handleClearFilters}
            style={webStyle.outlinedButton}
          >
            Clear
          </Button>
        </Grid>
      </Grid>
    );
  }

  renderMap() {
    const mappable = this.getMappableDrivers().length;
    return (
      <Box style={webStyle.mapWrapper} data-test-id="driverMap">
        <Box style={webStyle.mapHeader}>
          <Typography style={webStyle.mapNote}>
            {mappable} of {this.state.driverList.length} drivers reporting a
            position · refreshes every 12s
          </Typography>
        </Box>
        <div ref={this.setMapNode} style={webStyle.mapCanvas} />
      </Box>
    );
  }

  renderRow(driver: IDriverTracker) {
    const attrs = driver.attributes;
    const colors = this.getStateColor(attrs.tracker_state);
    const { location, workload, attendance } = attrs;

    return (
      <TableRow key={attrs.account_id} data-test-id="driverRow">
        <StyledCell>
          <Typography style={webStyle.driverName}>
            {attrs.driver.full_name || "--"}
          </Typography>
          <Typography style={webStyle.driverMeta}>
            {attrs.driver.full_phone_number || "--"}
          </Typography>
        </StyledCell>
        <StyledCell>
          <Chip
            label={this.formatLabel(attrs.tracker_state)}
            size="small"
            style={{
              backgroundColor: colors.bg,
              color: colors.color,
              fontWeight: 600,
              fontFamily: "Montserrat",
            }}
          />
        </StyledCell>
        <StyledCell>
          <Typography style={webStyle.cellText}>
            {this.formatLabel(attendance.status)}
          </Typography>
          <Typography style={webStyle.driverMeta}>
            {this.formatDuration(attendance.worked_seconds)} worked
          </Typography>
        </StyledCell>
        <StyledCell>
          <Typography style={webStyle.cellText}>
            {workload.active_requests} / {workload.max_concurrent}
          </Typography>
          {/* advisory only — auto-assignment ignores the ceiling unless
              ENFORCE_DRIVER_CAPACITY is on */}
          {workload.capacity_full && (
            <Typography style={webStyle.capacityHint}>At capacity</Typography>
          )}
        </StyledCell>
        <StyledCell>
          <Typography style={webStyle.cellText}>
            {location.fresh ? "Reporting" : "Stale"}
          </Typography>
          <Typography style={webStyle.driverMeta}>
            {this.formatAge(location)}
          </Typography>
        </StyledCell>
        <StyledCell style={{ maxWidth: 220 }}>
          {/* A driver can be attached to hundreds of stores; the full list would
              blow the row height out, so show a couple and count the rest. */}
          <Typography
            style={webStyle.cellText}
            title={attrs.stores.map((store) => store.store_name).join(", ")}
          >
            {this.summariseStores(attrs.stores)}
          </Typography>
        </StyledCell>
        <StyledCell>
          <Box display="flex" alignItems="center">
            <IconButton
              data-test-id={`detailBtn-${attrs.account_id}`}
              size="small"
              onClick={() => this.getDriverDetail(attrs.account_id)}
            >
              <VisibilityIcon style={{ color: "#204B9C" }} />
            </IconButton>
            {location.latitude !== null && location.longitude !== null && (
              <IconButton
                data-test-id={`navBtn-${attrs.account_id}`}
                size="small"
                onClick={() =>
                  this.openMapNavigation(
                    location.latitude as number,
                    location.longitude as number
                  )
                }
              >
                <NavigationIcon style={{ color: "#204B9C" }} />
              </IconButton>
            )}
          </Box>
        </StyledCell>
      </TableRow>
    );
  }

  renderTable() {
    const drivers = this.getMergedDrivers();
    const { isLoading, page, pageSize, totalCount } = this.state;

    return (
      <TableContainer component={Paper} style={webStyle.tableContainer}>
        <Table>
          <TableHead>
            <TableRow style={{ background: "#204B9C" }}>
              {[
                "Driver",
                "State",
                "Attendance",
                "Workload",
                "Location",
                "Stores",
                "Actions",
              ].map((heading) => (
                <HeaderCell key={heading}>{heading}</HeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {drivers.map((driver) => this.renderRow(driver))}
            {!drivers.length && !isLoading && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography style={webStyle.noDataText}>
                    No drivers found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box padding={4}>
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page - 1}
          onPageChange={this.handlePageChange}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[pageSize]}
        />
      </TableContainer>
    );
  }

  renderDetailDialog() {
    const {
      detailDialogOpen,
      detailData,
      detailHistory,
      detailRequests,
      detailLoading,
      detailError,
    } = this.state;
    const attrs = detailData?.attributes;

    return (
      <Dialog
        open={detailDialogOpen}
        onClose={this.handleCloseDetail}
        maxWidth="md"
        fullWidth
        data-test-id="driverDetailDialog"
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography style={webStyle.dialogTitle}>
              Driver {attrs ? `- ${attrs.driver.full_name}` : ""}
            </Typography>
            <IconButton onClick={this.handleCloseDetail} data-test-id="closeDriverDetail">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && (
            <Box display="flex" justifyContent="center" padding={4}>
              <CircularProgress />
            </Box>
          )}
          {!detailLoading && detailError && (
            <Typography style={webStyle.noDataText}>{detailError}</Typography>
          )}
          {!detailLoading && attrs && (
            <Grid container spacing={3}>
              <Grid item xs={6} md={3}>
                <DetailLabel>State</DetailLabel>
                <DetailValue>{this.formatLabel(attrs.tracker_state)}</DetailValue>
              </Grid>
              <Grid item xs={6} md={3}>
                <DetailLabel>Phone</DetailLabel>
                <DetailValue>{attrs.driver.full_phone_number || "--"}</DetailValue>
              </Grid>
              <Grid item xs={6} md={3}>
                <DetailLabel>Email</DetailLabel>
                <DetailValue>{attrs.driver.email || "--"}</DetailValue>
              </Grid>
              <Grid item xs={6} md={3}>
                <DetailLabel>Available</DetailLabel>
                <DetailValue>{attrs.is_available ? "Yes" : "No"}</DetailValue>
              </Grid>
              <Grid item xs={6} md={3}>
                <DetailLabel>Vehicle</DetailLabel>
                <DetailValue>
                  {attrs.vehicle_type || "--"}
                  {attrs.vehicle_plate ? ` · ${attrs.vehicle_plate}` : ""}
                </DetailValue>
              </Grid>
              <Grid item xs={6} md={3}>
                <DetailLabel>License</DetailLabel>
                <DetailValue>{attrs.license_number || "--"}</DetailValue>
              </Grid>
              <Grid item xs={6} md={3}>
                <DetailLabel>License Expiry</DetailLabel>
                <DetailValue>{this.formatDay(attrs.license_expiry)}</DetailValue>
              </Grid>
              <Grid item xs={6} md={3}>
                <DetailLabel>Last Fix</DetailLabel>
                <DetailValue>{this.formatAge(attrs.location)}</DetailValue>
              </Grid>

              {detailHistory && (
                <>
                  <Grid item xs={12}>
                    <Typography style={webStyle.sectionTitle}>
                      Attendance · {this.formatDay(detailHistory.from)} to{" "}
                      {this.formatDay(detailHistory.to)}
                    </Typography>
                    <Typography style={webStyle.driverMeta}>
                      {detailHistory.summary.days_present} days present ·{" "}
                      {detailHistory.summary.worked_hours}h worked ·{" "}
                      {this.formatDuration(detailHistory.summary.break_seconds)} on
                      break
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow style={{ background: "#204B9C" }}>
                            {["Date", "Status", "In", "Out", "Worked", "Break"].map(
                              (heading) => (
                                <HeaderCell key={heading}>{heading}</HeaderCell>
                              )
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detailHistory.records?.data?.map((record) => {
                            const row = record.attributes;
                            return (
                              <TableRow key={record.id}>
                                <TableCell>{this.formatDay(row.work_date)}</TableCell>
                                <TableCell>
                                  {this.formatLabel(row.status)}
                                  {/* an adjusted row was corrected by an admin */}
                                  {row.adjusted && (
                                    <Typography style={webStyle.adjustedHint}>
                                      Adjusted · punched{" "}
                                      {this.formatDate(row.original_checked_in_at)}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>{this.formatDate(row.checked_in_at)}</TableCell>
                                <TableCell>{this.formatDate(row.checked_out_at)}</TableCell>
                                <TableCell>{row.worked_hours ?? "--"}h</TableCell>
                                <TableCell>
                                  {this.formatDuration(row.break_seconds)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {!detailHistory.records?.data?.length && (
                            <TableRow>
                              <TableCell colSpan={6} align="center">
                                No attendance in this range
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Typography style={webStyle.sectionTitle}>
                  Active Jobs ({detailRequests.length})
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow style={{ background: "#204B9C" }}>
                        {["Request", "Status", "Leg", "Customer", "Store", "Assigned"].map(
                          (heading) => (
                            <HeaderCell key={heading}>{heading}</HeaderCell>
                          )
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailRequests.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell>{request.request_number}</TableCell>
                          <TableCell>{request.status_label}</TableCell>
                          <TableCell>{this.formatLabel(request.leg)}</TableCell>
                          <TableCell>{request.customer_name || "--"}</TableCell>
                          <TableCell>{request.store_name || "--"}</TableCell>
                          <TableCell>{this.formatDate(request.assigned_at)}</TableCell>
                        </TableRow>
                      ))}
                      {!detailRequests.length && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No active jobs
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    );
  }
  // Customizable Area End

  render() {
    // Customizable Area Start
    return (
      <Box style={webStyle.mainWrapper}>
        <Box style={webStyle.headerRow}>
          <Typography style={webStyle.pageTitle}>Driver Tracker</Typography>
          <Button
            data-test-id="refreshBtn"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={this.handleRefresh}
            style={webStyle.outlinedButton}
          >
            Refresh
          </Button>
        </Box>

        {this.renderSummary()}
        {this.renderFilters()}

        <Tabs
          data-test-id="driverTrackerTabs"
          value={this.state.activeTab}
          onChange={this.handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          style={webStyle.tabsRow}
        >
          <Tab data-test-id="driverListTab" label="Drivers" style={webStyle.tab} />
          <Tab data-test-id="driverMapTab" label="Live Map" style={webStyle.tab} />
        </Tabs>

        {/* Only the active panel is mounted; setMapNode rebuilds the map when
            the map tab comes back. */}
        {this.state.activeTab === 0 ? this.renderTable() : this.renderMap()}
        {this.renderDetailDialog()}

        <Snackbar
          open={this.state.snackbarOpen}
          autoHideDuration={4000}
          onClose={this.handleSnackbarClose}
        >
          <Alert severity={this.state.snackbarSeverity}>
            {this.state.snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    );
    // Customizable Area End
  }
}

// Customizable Area Start
const HeaderCell = styled(TableCell)({
  fontFamily: "Montserrat",
  fontWeight: 600,
  fontSize: 14,
  color: "#FFFFFF",
});

const StyledCell = styled(TableCell)({
  fontFamily: "Montserrat",
  fontSize: 14,
  color: "#1A1A1A",
  padding: "12px 16px",
  borderBottom: "1px solid #E2E8F0",
});

const DetailLabel = styled(Typography)({
  fontFamily: "Montserrat",
  fontWeight: 600,
  fontSize: 13,
  color: "#64748B",
  marginBottom: 4,
});

const DetailValue = styled(Typography)({
  fontFamily: "Montserrat",
  fontSize: 15,
  color: "#1A1A1A",
});

const webStyle: { [key: string]: React.CSSProperties } = {
  mainWrapper: {
    fontFamily: "Montserrat",
    padding: "24px 48px",
    background: "#FFFFFF",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  pageTitle: {
    fontFamily: "Montserrat",
    fontSize: 24,
    fontWeight: 600,
    color: "#1A1A1A",
  },
  sectionTitle: {
    fontFamily: "Montserrat",
    fontSize: 16,
    fontWeight: 600,
    color: "#1A1A1A",
  },
  tile: {
    border: "1px solid #E2E8F0",
    borderRadius: 12,
    padding: "12px 16px",
  },
  tileLabel: {
    fontFamily: "Montserrat",
    fontSize: 13,
    fontWeight: 600,
    color: "#64748B",
  },
  tileValue: {
    fontFamily: "Montserrat",
    fontSize: 22,
    fontWeight: 600,
    color: "#1A1A1A",
  },
  pillRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 20,
  },
  tabsRow: {
    borderBottom: "1px solid #E2E8F0",
    marginBottom: 16,
    minHeight: 40,
  },
  tab: {
    fontFamily: "Montserrat",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none" as const,
    minHeight: 40,
    minWidth: 120,
  },
  mapWrapper: {
    border: "1px solid #E2E8F0",
    borderRadius: 12,
    overflow: "hidden",
    marginBottom: 24,
  },
  mapHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    padding: "12px 16px",
    borderBottom: "1px solid #E2E8F0",
  },
  mapNote: {
    fontFamily: "Montserrat",
    fontSize: 13,
    color: "#64748B",
  },
  mapCanvas: {
    width: "100%",
    height: 360,
  },
  tableContainer: {
    borderRadius: 12,
    boxShadow:
      "0px 4px 8px rgba(0, 0, 0, 0.03), 0px 8px 32px rgba(0, 0, 0, 0.06)",
  },
  driverName: {
    fontFamily: "Montserrat",
    fontSize: 14,
    fontWeight: 600,
    color: "#1A1A1A",
  },
  driverMeta: {
    fontFamily: "Montserrat",
    fontSize: 12,
    color: "#64748B",
  },
  cellText: {
    fontFamily: "Montserrat",
    fontSize: 14,
    color: "#1A1A1A",
  },
  capacityHint: {
    fontFamily: "Montserrat",
    fontSize: 12,
    color: "#92400E",
  },
  adjustedHint: {
    fontFamily: "Montserrat",
    fontSize: 11,
    color: "#92400E",
  },
  noDataText: {
    fontFamily: "Montserrat",
    fontSize: 16,
    color: "#64748B",
    padding: 32,
  },
  dialogTitle: {
    fontFamily: "Montserrat",
    fontSize: 20,
    fontWeight: 600,
  },
  outlinedButton: {
    borderColor: "#204B9C",
    color: "#204B9C",
    fontFamily: "Montserrat",
    fontWeight: 600,
    textTransform: "none" as const,
  },
};
// Customizable Area End
